import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MdDownload, MdReceipt } from 'react-icons/md';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchUserProfile } from '../store/slices/userSlice';
import { invoiceProfile } from '../utils/helpers';
import { generateInvoicePDF, type InvoiceFields } from '../components/page-components/Dashboard/SessionDetailsModal/DownloadInvoicePDF';

export interface InvoicePageSession {
    id: string;
    type: 'solo' | 'team';
    goal: string;
    duration: number;
    completedAt: string;
    startedAt?: string;
    notes?: string | string[];
    members?: string[];
    music?: string;
    tasks?: { task: string; isCompleted: boolean }[];
}

export interface InvoicePageState {
    session: InvoicePageSession;
    clientName: string;
    clientAddress: string;
    currency: string;
    hourlyCharge: number | '';
    invoiceNumber: string;
    invoiceDate: string;
    senderName: string;
    invoiceSessionGoal: string;
    invoiceTasks: string[];
    invoiceTotalMinutes: number;
}

const CURRENCIES = [
    { value: 'USD', label: 'USD ($)' },
    { value: 'EUR', label: 'EUR (€)' },
    { value: 'GBP', label: 'GBP (£)' },
    { value: 'INR', label: 'INR (₹)' },
    { value: 'JPY', label: 'JPY (¥)' },
    { value: 'CAD', label: 'CAD (C$)' },
    { value: 'AUD', label: 'AUD (A$)' },
];

const formatDuration = (durationInMinutes: number): string => {
    const mins = Math.floor(durationInMinutes);
    const secs = Math.round((durationInMinutes - mins) * 60);
    if (mins === 0 && secs === 0) return '0s';
    if (mins === 0) return `${secs}s`;
    if (secs === 0) return `${mins}m`;
    return `${mins}m ${secs}s`;
};

const InvoicePage: React.FC = () => {
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.user);
    const state = location.state as InvoicePageState | null;
    const [clientName, setClientName] = useState('');
    const [clientAddress, setClientAddress] = useState('');
    const [currency, setCurrency] = useState('USD');
    const [hourlyCharge, setHourlyCharge] = useState<number | ''>('');
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [invoiceDate, setInvoiceDate] = useState('');
    const [senderName, setSenderName] = useState('');
    const [senderAddress, setSenderAddress] = useState('');
    const [senderEmail, setSenderEmail] = useState('');
    const [invoiceSessionGoal, setInvoiceSessionGoal] = useState('');
    const [invoiceTasks, setInvoiceTasks] = useState<string[]>([]);
    const [invoiceTotalMinutes, setInvoiceTotalMinutes] = useState(0);
    const [payeeName, setPayeeName] = useState('');
    const [bankName, setBankName] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [ifscCode, setIfscCode] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (!user) dispatch(fetchUserProfile());
    }, [dispatch, user]);

    useEffect(() => {
        if (state?.session) {
            setClientName(state.clientName || '');
            setClientAddress(state.clientAddress || '');
            setCurrency(state.currency || 'USD');
            setHourlyCharge(state.hourlyCharge ?? '');
            setInvoiceNumber(state.invoiceNumber || '');
            setInvoiceDate(state.invoiceDate || '');
            if ((state as any).senderName ?? (state as any).freelancerName) {
                setSenderName((state as any).senderName ?? (state as any).freelancerName ?? '');
            }
            setInvoiceSessionGoal(state.invoiceSessionGoal || state.session.goal);
            setInvoiceTasks(state.invoiceTasks?.length ? state.invoiceTasks.map((t) => (typeof t === 'string' ? t : (t as { task: string }).task)) : (state.session.tasks ? state.session.tasks.map((t) => t.task) : []));
            setInvoiceTotalMinutes(state.invoiceTotalMinutes ?? state.session.duration);
        } else if (state && 'dateFrom' in state) {
            const workLogState = state as { dateFrom?: string; dateTo?: string };
            if (workLogState.dateFrom) setInvoiceDate(workLogState.dateFrom);
        }
    }, [state]);

    useEffect(() => {
        const profile = invoiceProfile.get();
        const fromUser = user?.name || user?.email || user?.address;
        const fromProfile = profile.senderName || profile.senderAddress || profile.senderEmail || profile.payeeName;
        if (fromUser || fromProfile) {
            setSenderName((prev) => prev || user?.name || profile.senderName || '');
            setSenderAddress((prev) => prev || user?.address || profile.senderAddress || '');
            setSenderEmail((prev) => prev || user?.email || profile.senderEmail || '');
            setPayeeName((prev) => prev || user?.bankDetails?.payeeName || profile.payeeName || '');
            setBankName((prev) => prev || user?.bankDetails?.bankName || profile.bankName || '');
            setAccountNumber((prev) => prev || user?.bankDetails?.accountNumber || profile.accountNumber || '');
            setIfscCode((prev) => prev || user?.bankDetails?.ifscCode || profile.ifscCode || '');
        }
    }, [user]);

    const session = state?.session ?? {
        id: 'manual-invoice',
        type: 'solo' as const,
        goal: invoiceSessionGoal || '',
        duration: invoiceTotalMinutes || 0,
        completedAt: invoiceDate ? new Date(invoiceDate).toISOString() : new Date().toISOString(),
        members: [],
        tasks: [],
    };

    const invoiceDurationMinutes = (invoiceTotalMinutes > 0 ? invoiceTotalMinutes : session.duration) || 0;
    const durationHours = invoiceDurationMinutes / 60;
    const totalAmount = typeof hourlyCharge === 'number' && hourlyCharge >= 0 ? hourlyCharge * durationHours : 0;

    const handleDownloadPDF = async () => {
        setError('');
        const tasksList = invoiceTasks.filter(Boolean);
        const errors: string[] = [];
        if (!senderName?.trim()) errors.push('Your name is required.');
        if (!senderAddress?.trim()) errors.push('Your address is required.');
        if (!senderEmail?.trim()) errors.push('Your email is required.');
        if (!clientName?.trim()) errors.push('Client name is required.');
        if (!clientAddress?.trim()) errors.push('Client address is required.');
        if (!invoiceSessionGoal?.trim()) errors.push('Description or Subject is required.');
        if (tasksList.length === 0) errors.push('Add at least one task before generating the invoice.');
        if (!invoiceTotalMinutes || invoiceTotalMinutes <= 0) errors.push('Total time (minutes) must be greater than 0.');
        if (typeof hourlyCharge !== 'number' || hourlyCharge < 0) errors.push('Enter a valid hourly rate (≥ 0).');
        if (!invoiceNumber?.trim()) errors.push('Invoice number is required.');
        if (!invoiceDate?.trim()) errors.push('Invoice date is required.');
        if (errors.length > 0) {
            setError(errors.length === 1 ? errors[0] : `Please fix the following:\n${errors.map((e, i) => `${i + 1}. ${e}`).join('\n')}`);
            return;
        }
        const result = await generateInvoicePDF(session, {
            clientName,
            currency,
            hourlyCharge: typeof hourlyCharge === 'number' ? hourlyCharge : 0,
            invoiceNumber: invoiceNumber || undefined,
            invoiceDate: invoiceDate || undefined,
            senderName: senderName || undefined,
            senderAddress: senderAddress.trim() || undefined,
            senderEmail: senderEmail.trim() || undefined,
            clientAddress: clientAddress.trim() || undefined,
            sessionGoalOverride: invoiceSessionGoal.trim() || undefined,
            tasksOverride: invoiceTasks.filter(Boolean),
            totalMinutesOverride: invoiceTotalMinutes,
            payeeName: payeeName.trim() || undefined,
            bankName: bankName.trim() || undefined,
            accountNumber: accountNumber.trim() || undefined,
            ifscCode: ifscCode.trim() || undefined,
        });
        if (!result.success) {
            setError(result.error || 'Failed to generate invoice.');
        } else {
            invoiceProfile.set({
                senderName: senderName || undefined,
                senderAddress: senderAddress.trim() || undefined,
                senderEmail: senderEmail.trim() || undefined,
                payeeName: payeeName.trim() || undefined,
                bankName: bankName.trim() || undefined,
                accountNumber: accountNumber.trim() || undefined,
                ifscCode: ifscCode.trim() || undefined,
            });
        }
    };

    return (
        <div className="min-h-screen bg-[#FFF8F0] p-4 sm:p-6">
            <div className="max-w-2xl mx-auto">
                <div className="bg-white border border-gray-200/60 rounded-xl p-5 sm:p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                        <MdReceipt className="text-lg text-orange-500/70" />
                        <h1 className="text-lg font-semibold text-gray-900">Generate invoice</h1>
                    </div>
                    <p className="text-sm text-gray-600 font-light mb-4">
                        Fill in the details below to download your invoice PDF.
                    </p>

                    <div className="space-y-4 mb-6">
                        <h2 className="text-sm font-semibold text-gray-800 pt-2 border-t border-gray-100">From (You)</h2>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Your name</label>
                            <input
                                type="text"
                                value={senderName}
                                onChange={(e) => setSenderName(e.target.value)}
                                placeholder="e.g. John Doe"
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/50 outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Your address</label>
                            <textarea
                                value={senderAddress}
                                onChange={(e) => setSenderAddress(e.target.value)}
                                placeholder="Street, city, postal code, country"
                                rows={2}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/50 outline-none resize-none"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Your email</label>
                            <input
                                type="email"
                                value={senderEmail}
                                onChange={(e) => setSenderEmail(e.target.value)}
                                placeholder="your@email.com"
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/50 outline-none"
                            />
                        </div>

                        <h2 className="text-sm font-semibold text-gray-800 pt-2 border-t border-gray-100">Bill To (Client)</h2>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Client name</label>
                            <input
                                type="text"
                                value={clientName}
                                onChange={(e) => setClientName(e.target.value)}
                                placeholder="Client or company name"
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/50 outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Client address</label>
                            <textarea
                                value={clientAddress}
                                onChange={(e) => setClientAddress(e.target.value)}
                                placeholder="Street, city, postal code, country"
                                rows={2}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/50 outline-none resize-none"
                            />
                        </div>

                        <h2 className="text-sm font-semibold text-gray-800 pt-2 border-t border-gray-100">Invoice details</h2>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Description or Subject</label>
                            <input
                                type="text"
                                value={invoiceSessionGoal}
                                onChange={(e) => setInvoiceSessionGoal(e.target.value)}
                                placeholder="e.g. Professional service bill August 2024"
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/50 outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Tasks</label>
                            <div className="space-y-2">
                                {invoiceTasks.map((task, i) => (
                                    <div key={i} className="flex gap-2 items-center flex-wrap">
                                        <input
                                            type="text"
                                            value={task}
                                            onChange={(e) => {
                                                const next = [...invoiceTasks];
                                                next[i] = e.target.value;
                                                setInvoiceTasks(next);
                                            }}
                                            placeholder="Task name"
                                            className="flex-1 min-w-[120px] px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/50 outline-none"
                                        />
                                        <button type="button" onClick={() => setInvoiceTasks((prev) => prev.filter((_, idx) => idx !== i))} className="p-2 text-gray-400 hover:text-red-600 rounded" title="Remove task">×</button>
                                    </div>
                                ))}
                                <button type="button" onClick={() => setInvoiceTasks((prev) => [...prev, ''])} className="text-xs font-medium text-orange-600 hover:text-orange-700">+ Add task</button>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Total time (minutes)</label>
                                <input
                                    type="number"
                                    min={0}
                                    value={invoiceTotalMinutes || ''}
                                    onChange={(e) => setInvoiceTotalMinutes(Math.max(0, parseFloat(e.target.value) || 0))}
                                    placeholder="e.g. 2760"
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/50 outline-none"
                                />
                                <span className="text-xs text-gray-500 mt-1 block">= {formatDuration(invoiceTotalMinutes || 0)} ({(invoiceTotalMinutes || 0) / 60} h)</span>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Hourly rate</label>
                                <input
                                    type="number"
                                    min={0}
                                    step={0.01}
                                    value={hourlyCharge === '' ? '' : hourlyCharge}
                                    onChange={(e) => setHourlyCharge(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                                    placeholder="0.00"
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/50 outline-none"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Invoice number</label>
                                <input
                                    type="text"
                                    value={invoiceNumber}
                                    onChange={(e) => setInvoiceNumber(e.target.value)}
                                    placeholder="e.g. 2024-25/04"
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/50 outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Invoice date</label>
                                <input
                                    type="date"
                                    value={invoiceDate}
                                    onChange={(e) => setInvoiceDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/50 outline-none"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Currency</label>
                            <select
                                value={currency}
                                onChange={(e) => setCurrency(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/50 outline-none bg-white"
                            >
                                {CURRENCIES.map((c) => (
                                    <option key={c.value} value={c.value}>{c.label}</option>
                                ))}
                            </select>
                        </div>

                        <h2 className="text-sm font-semibold text-gray-800 pt-2 border-t border-gray-100">Bank details</h2>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Payee name</label>
                            <input
                                type="text"
                                value={payeeName}
                                onChange={(e) => setPayeeName(e.target.value)}
                                placeholder="Name as on bank account"
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/50 outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Bank name</label>
                            <input
                                type="text"
                                value={bankName}
                                onChange={(e) => setBankName(e.target.value)}
                                placeholder="e.g. HDFC Bank"
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/50 outline-none"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Account number</label>
                                <input
                                    type="text"
                                    value={accountNumber}
                                    onChange={(e) => setAccountNumber(e.target.value)}
                                    placeholder="e.g. 50100296774501"
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/50 outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">IFSC code</label>
                                <input
                                    type="text"
                                    value={ifscCode}
                                    onChange={(e) => setIfscCode(e.target.value)}
                                    placeholder="e.g. HDFC0000520"
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/50 outline-none"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-4 mb-4 p-3 bg-orange-50/50 border border-orange-200/40 rounded-lg">
                        <div>
                            <span className="text-xs text-gray-500 block">Hours</span>
                            <span className="text-sm font-medium text-gray-900">{durationHours.toFixed(2)} h</span>
                        </div>
                        <div>
                            <span className="text-xs text-gray-500 block">Sub total</span>
                            <span className="text-sm font-medium text-gray-900">{currency} {totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                        </div>
                    </div>

                    {error && (
                        <div role="alert" className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                            {error.includes('\n') ? (
                                <ul className="list-disc list-inside space-y-1">
                                    {error.split('\n').filter(Boolean).map((line, i) => (
                                        <li key={i}>{line.replace(/^\d+\.\s*/, '')}</li>
                                    ))}
                                </ul>
                            ) : (
                                error
                            )}
                        </div>
                    )}
                    <button
                        onClick={handleDownloadPDF}
                        disabled={invoiceTasks.filter(Boolean).length === 0}
                        className="px-4 py-2.5 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium flex items-center gap-2"
                    >
                        <MdDownload className="text-lg" />
                        Download invoice PDF
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InvoicePage;
